import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GET_WORK_ORDERS, GET_PROPERTY_RESERVATIONS } from '../graphql/queries';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import WorkOrderDetailSlidePanel from '../components/work-orders/WorkOrderDetailSlidePanel';
import { formatDueDate } from '../utils/date-utils';
import { AreaIndicator } from '../components/job-indicators';
import { UserRoleLabels } from '../utils/enums';
import { OccupancyBadge, NextCheckInRow } from '../components/work-orders/WorkOrderOccupancyBadges';
import WorkOrderActiveTimerIndicator from '../components/work-orders/WorkOrderActiveTimerIndicator';
import { useAuth } from '../contexts/AuthContext';

const ITEMS_PER_PAGE = 25;

const AREA_GROUPS = [
  {
    value: 'kiawah_seabrook',
    label: 'Kiawah + Seabrook',
    areas: ['KIAWAH', 'SEABROOK_ISLAND']
  },
  {
    value: 'iop_wild_dunes',
    label: 'Isle of Palms + Wild Dunes',
    areas: ['ISLE_OF_PALMS', 'ISLE_OF_PALMS_WILD_DUNES']
  }
];

const DEPARTMENT_OPTIONS = [
  {
    value: 'property_management',
    label: 'Property Management',
    roles: ['PROPERTY_MANAGER', 'MAINTENANCE']
  },
  {
    value: 'housekeeping',
    label: 'Housekeeping',
    roles: ['HOUSEKEEPER_MANAGER']
  }
];

// Roles that should default the department filter to their own department.
// Admins / call-center users see all departments by default.
const DEFAULT_DEPARTMENT_BY_ROLE = {
  HOUSEKEEPER_MANAGER: 'housekeeping',
  PROPERTY_MANAGER: 'property_management',
  MAINTENANCE: 'property_management'
};

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELED', label: 'Canceled' }
];

const getStatusColor = (status) => {
  switch (status) {
    case 'OPEN':
      return 'bg-yellow-100 text-yellow-800';
    case 'IN_PROGRESS':
      return 'bg-blue-100 text-blue-800';
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'CANCELED':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function AllStatusWorkOrders() {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [hasUserInteractedWithDepartment, setHasUserInteractedWithDepartment] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const isCallCenter = currentUser?.role === 'CALL_CENTER';
  const [reservationistOnly, setReservationistOnly] = useState(isCallCenter);

  useEffect(() => {
    if (hasUserInteractedWithDepartment) return;
    const roleDefault = DEFAULT_DEPARTMENT_BY_ROLE[currentUser?.role];
    if (!roleDefault) return;
    setDepartmentFilter((previous) => (previous ? previous : roleDefault));
  }, [currentUser?.role, hasUserInteractedWithDepartment]);

  const handleDepartmentChange = (value) => {
    setHasUserInteractedWithDepartment(true);
    setDepartmentFilter(value);
  };

  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);

  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [isSlidePanelOpen, setIsSlidePanelOpen] = useState(false);

  // Property reservations for Unoccupied / Next Check-in badges
  // (loaded lazily for the property IDs visible in the list).
  const [reservationsByProperty, setReservationsByProperty] = useState({});
  const [fetchPropertyReservations] = useLazyQuery(GET_PROPERTY_RESERVATIONS, {
    fetchPolicy: 'network-only'
  });

  // Debounce the search input so we don't refetch on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getSelectedAreas = () => {
    if (!areaFilter) return undefined;
    const group = AREA_GROUPS.find((g) => g.value === areaFilter);
    return group?.areas;
  };

  const getSelectedRoles = () => {
    if (!departmentFilter) return undefined;
    const dept = DEPARTMENT_OPTIONS.find((d) => d.value === departmentFilter);
    return dept?.roles;
  };

  // statuses array filter -> if a single status is picked, send [status]; otherwise undefined = all statuses.
  const getQueryStatuses = () => {
    if (showDeleted) return undefined;
    if (statusFilter) return [statusFilter];
    return undefined;
  };

  // Variables for the work orders query. Reset to page 1 whenever filters change.
  const queryInput = {
    limit: ITEMS_PER_PAGE,
    page: 1,
    search: debouncedSearch || undefined,
    statuses: getQueryStatuses(),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    areas: getSelectedAreas(),
    assignedRoles: getSelectedRoles(),
    sortOrder: 'recent',
    includeDeleted: showDeleted || undefined,
    submittedByRoles: reservationistOnly ? ['CALL_CENTER'] : undefined
  };

  const { loading, error, data, fetchMore, refetch } = useQuery(GET_WORK_ORDERS, {
    variables: { input: queryInput },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const workOrders = data?.workOrders?.items || [];
  const totalResults = data?.workOrders?.total || 0;

  // When filters change, refetch resets us to page 1.
  useEffect(() => {
    setPage(1);
    setAllLoaded(false);
  }, [debouncedSearch, statusFilter, dateFrom, dateTo, areaFilter, departmentFilter, showDeleted, reservationistOnly]);

  // Track whether all items are loaded based on returned count vs total.
  useEffect(() => {
    if (!data?.workOrders) return;
    setAllLoaded(workOrders.length >= totalResults);
  }, [workOrders.length, totalResults, data]);

  // Lazily fetch reservations for the property IDs we haven't loaded yet so the
  // Unoccupied / Next check-in badges can render (mirrors WorkOrders.js).
  useEffect(() => {
    const propertyIds = [...new Set(workOrders.map((wo) => wo.property?.id).filter(Boolean))];
    const idsToFetch = propertyIds.filter((id) => !reservationsByProperty[id]);
    if (idsToFetch.length === 0) return;
    const fetchAll = async () => {
      const results = await Promise.allSettled(
        idsToFetch.map((id) => fetchPropertyReservations({ variables: { propertyId: id } }))
      );
      setReservationsByProperty((prev) => {
        const next = { ...prev };
        results.forEach((result, i) => {
          if (result.status === 'fulfilled' && result.value?.data?.propertyReservations) {
            next[idsToFetch[i]] = result.value.data.propertyReservations;
          }
        });
        return next;
      });
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || allLoaded || loading) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    try {
      await fetchMore({
        variables: { input: { ...queryInput, page: nextPage } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.workOrders) return prev;
          return {
            workOrders: {
              ...fetchMoreResult.workOrders,
              items: [...(prev?.workOrders?.items || []), ...fetchMoreResult.workOrders.items]
            }
          };
        }
      });
      setPage(nextPage);
    } catch (err) {
      // Apollo will surface error via the hook; swallowing here to avoid breaking the loader.
      // eslint-disable-next-line no-console
      console.error('Error loading more work orders:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, allLoaded, loading, page, fetchMore, queryInput]);

  // Infinite scroll - observe a sentinel div at the bottom of the list.
  // We keep `handleLoadMore` behind a ref so the observer is attached once
  // (per sentinel mount) and never recreated on each render, which would
  // otherwise cascade-fire while the sentinel stays in view.
  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(handleLoadMore);
  useEffect(() => {
    loadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);
  const hasItems = workOrders.length > 0;
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreRef.current?.();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasItems]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setAreaFilter('');
    setDepartmentFilter('');
    setHasUserInteractedWithDepartment(true);
    setShowDeleted(false);
    setReservationistOnly(false);
    setPage(1);
    setAllLoaded(false);
  };

  const handleOpenSlidePanel = (workOrderId) => {
    setSelectedWorkOrderId(workOrderId);
    setIsSlidePanelOpen(true);
  };

  const handleCloseSlidePanel = useCallback(() => {
    setIsSlidePanelOpen(false);
    setSelectedWorkOrderId(null);
  }, []);

  const handleWorkOrderDataChange = useCallback(() => {
    refetch();
  }, [refetch]);

  const hasActiveFilters =
    Boolean(searchTerm) ||
    Boolean(statusFilter) ||
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    Boolean(areaFilter) ||
    Boolean(departmentFilter) ||
    showDeleted ||
    reservationistOnly;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-center space-x-3">
          <Link
            to="/dashboard/work-orders"
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium border border-white/30 text-white hover:bg-white/10 lg:border-gray-300 lg:text-gray-700 lg:bg-white lg:hover:bg-gray-50 lg:hover:border-gray-400 shadow-sm transition-colors"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Active Work Orders
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-white lg:text-gray-900 flex items-center">
            <svg className="h-5 w-5 mr-1.5 text-blue-300 lg:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            All Work Orders
          </h1>
        </div>
        <div className="text-sm text-white/70 lg:text-gray-500">
          {totalResults > 0
            ? `Showing ${workOrders.length} of ${totalResults}`
            : null}
        </div>
      </div>

      {/* Filters - Accordion */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className="w-full px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
          aria-expanded={isFiltersOpen}
          aria-controls="all-status-work-orders-filters"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-800">Filters</h3>
              {hasActiveFilters && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {[searchTerm, statusFilter, dateFrom, dateTo, areaFilter, departmentFilter, showDeleted, reservationistOnly].filter(Boolean).length} active
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {hasActiveFilters && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); handleClearFilters(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleClearFilters(); } }}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Reset all filters"
                >
                  Reset all
                </span>
              )}
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </button>

        {isFiltersOpen && (
          <div id="all-status-work-orders-filters" className="p-5 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
              <div className="md:col-span-3">
                <label htmlFor="aswo-search" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Search
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="aswo-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Search by description or property name..."
                  />
                </div>
              </div>

              <div>
                <label htmlFor="aswo-area" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Area
                </label>
                <select
                  id="aswo-area"
                  value={areaFilter}
                  onChange={(e) => setAreaFilter(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="">All Areas</option>
                  {AREA_GROUPS.map((group) => (
                    <option key={group.value} value={group.value}>{group.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="aswo-department" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Department
                </label>
                <select
                  id="aswo-department"
                  value={departmentFilter}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="">All Departments</option>
                  {DEPARTMENT_OPTIONS.map((dept) => (
                    <option key={dept.value} value={dept.value}>{dept.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="aswo-status" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Status
                </label>
                <select
                  id="aswo-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="">All Statuses</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="aswo-date-from" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  From Date
                </label>
                <input
                  type="date"
                  id="aswo-date-from"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="aswo-date-to" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  To Date
                </label>
                <input
                  type="date"
                  id="aswo-date-to"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>

              <div className="flex items-end pb-1">
                <label className="relative inline-flex items-center cursor-pointer" htmlFor="aswo-reservationist-toggle">
                  <input
                    type="checkbox"
                    id="aswo-reservationist-toggle"
                    checked={reservationistOnly}
                    onChange={() => setReservationistOnly(!reservationistOnly)}
                    className="sr-only peer"
                    aria-label="Toggle showing only reservationist submitted work orders"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Reservationist Submitted
                  </span>
                </label>
              </div>

              <div className="flex items-end pb-1">
                <label className="relative inline-flex items-center cursor-pointer" htmlFor="aswo-show-deleted-toggle">
                  <input
                    type="checkbox"
                    id="aswo-show-deleted-toggle"
                    checked={showDeleted}
                    onChange={() => setShowDeleted(!showDeleted)}
                    className="sr-only peer"
                    aria-label="Toggle showing deleted work orders"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Show Deleted
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Work Orders List */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        {error ? (
          <div className="p-6 text-center text-red-600">
            <p>Error loading work orders: {error.message}</p>
          </div>
        ) : workOrders.length === 0 && !loading ? (
          <div className="p-10 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm">
              {hasActiveFilters
                ? 'No work orders match your filters.'
                : 'No work orders found.'}
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200">
              {workOrders.map((workOrder, index) => {
                const isDeletedRow = showDeleted && workOrder.isDeleted;
                const rowBg = isDeletedRow
                  ? 'bg-red-50/40'
                  : workOrder.isUrgent
                    ? 'bg-red-50'
                    : index % 2 === 0
                      ? 'bg-white'
                      : 'bg-gray-50';
                return (
                  <li key={workOrder.id} className={`p-4 ${rowBg}`}>
                    <div className="flex flex-col space-y-2">
                      <WorkOrderActiveTimerIndicator hasActiveTimer={workOrder.hasActiveTimer} />
                      {/* Title + indicators */}
                      <div className="flex justify-between items-start gap-2">
                        <div className="text-sm font-medium text-gray-900">{workOrder.property?.name}</div>
                        <div className="flex items-center flex-wrap justify-end gap-y-1">
                          {workOrder.isUrgent && (
                            <span className="inline-flex items-center px-2.5 py-0.5 ml-2 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Urgent
                            </span>
                          )}
                          {workOrder.isAfterHours && (
                            <span className="inline-flex items-center px-2.5 py-0.5 ml-2 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              After Hours
                            </span>
                          )}
                          {workOrder.streamlineWorkOrderId && (
                            <span className="inline-flex items-center px-2.5 py-0.5 ml-2 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Streamline
                            </span>
                          )}
                          {workOrder.isGuestSubmitted && (
                            <span className="inline-flex items-center px-2.5 py-0.5 ml-2 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              Guest
                            </span>
                          )}
                          <span className="ml-2">
                            <OccupancyBadge propertyId={workOrder.property?.id} reservationsByProperty={reservationsByProperty} />
                          </span>
                          {isDeletedRow ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 ml-2 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              DELETED
                            </span>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 ml-2 rounded-full text-xs font-medium ${getStatusColor(workOrder.status)}`}>
                              {workOrder.status.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Comment count alert */}
                      {workOrder.commentCount > 0 && (
                        <div className={`flex items-center p-2 border-l-4 border-t border-r border-b rounded-md mb-2 ${
                          workOrder.urgentCommentCount > 0
                            ? 'bg-orange-50 border-orange-500'
                            : 'bg-blue-50 border-blue-500'
                        }`}>
                          <svg className={`h-5 w-5 mr-2 flex-shrink-0 ${
                            workOrder.urgentCommentCount > 0 ? 'text-orange-700' : 'text-blue-700'
                          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span className={`text-sm font-medium ${
                            workOrder.urgentCommentCount > 0 ? 'text-orange-800' : 'text-blue-800'
                          }`}>
                            {workOrder.urgentCommentCount > 0
                              ? `${workOrder.urgentCommentCount} Urgent Comment${workOrder.urgentCommentCount > 1 ? 's' : ''}`
                              : `${workOrder.commentCount} Comment${workOrder.commentCount > 1 ? 's' : ''}`
                            }
                          </span>
                        </div>
                      )}

                      {/* Date */}
                      <div className="text-xs text-gray-500">
                        {isDeletedRow && workOrder.deletedAt
                          ? `Deleted: ${format(new Date(workOrder.deletedAt), 'MMM d, yyyy')}`
                          : `Submitted: ${format(new Date(workOrder.createdAt), 'MMM d, yyyy')}`}
                        {workOrder.dueDate && (
                          <span className="ml-2 text-blue-600">
                            • Due: {formatDueDate(workOrder.dueDate)}
                          </span>
                        )}
                      </div>

                      {/* Assignment indicator */}
                      {workOrder.assignedTo ? (
                        <div className="flex items-center py-1 px-2 bg-blue-50 rounded text-xs text-blue-800 w-fit">
                          <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span>Assigned to: {workOrder.assignedTo?.name} ({UserRoleLabels[workOrder.assignedTo?.role] || workOrder.assignedTo?.role})</span>
                        </div>
                      ) : (
                        <div className="flex items-center py-1 px-2 bg-gray-50 rounded text-xs text-gray-600 w-fit">
                          <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Unassigned</span>
                        </div>
                      )}

                      {/* Description (full width) */}
                      <p className="text-sm text-gray-900 line-clamp-2">{workOrder.description}</p>

                      {/* Next check-in row (when unoccupied) */}
                      <NextCheckInRow propertyId={workOrder.property?.id} reservationsByProperty={reservationsByProperty} />

                      {/* Footer: area + action */}
                      <div className="flex justify-between items-end pt-2">
                        <div className="flex items-center">
                          {workOrder.property?.area && (
                            <AreaIndicator area={workOrder.property.area} />
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handleOpenSlidePanel(workOrder.id)}
                            className="inline-flex items-center px-3 py-1 border border-blue-600 text-blue-600 rounded-md text-sm hover:bg-blue-50 cursor-pointer"
                            aria-label={`View details for ${workOrder.property?.name}`}
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Loading more indicator + sentinel for infinite scroll */}
            <div ref={sentinelRef} className="py-4 flex justify-center items-center border-t border-gray-100">
              {(loading || isLoadingMore) && (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-sm text-gray-500">Loading...</span>
                </>
              )}
              {!loading && !isLoadingMore && allLoaded && workOrders.length > 0 && (
                <span className="text-xs text-gray-400">All {totalResults} work orders loaded.</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Work Order Detail Slide Panel */}
      <WorkOrderDetailSlidePanel
        isOpen={isSlidePanelOpen}
        onClose={handleCloseSlidePanel}
        workOrderId={selectedWorkOrderId}
        onDataChange={handleWorkOrderDataChange}
      />
    </div>
  );
}
